/* eslint-disable max-len */
import { AlertTriangleIcon, InfoIcon, LinkIcon } from 'assets/icons';

import { Button, ButtonSize, ButtonStyle, ButtonType } from 'common/button';
import {
  calculateHoursAndMinutes,
  canUpgradeVault,
  classnames,
  isFreeTrial,
  isOwner,
  isTemporaryUser,
} from 'helpers/utils';
import { ProgressBar } from 'common/progress-bar';
import { HelperText } from 'common/helper-text';
import { useContext, useEffect, useState } from 'react';
import { goToPage, RouteName } from 'routes';
import { AppContext, SensorSelectionContext } from 'context';
import {
  apiBaseURL,
  msMaxPercentageToAlertVideoCallTime,
} from 'config/constants';
import { InfoModal } from 'common/info-modal';
import {
  calculateWaitingRoomModalInstructions,
  calculateWaitingRoomPersistentInstructions,
} from 'common/sensor-permission-instructions';
import { useLoadSensorSelection } from 'hooks/use-load-sensor-selection';
import { CopyLink } from 'common/copy-link';
import {
  VideoCallRole,
  useVideocallWebsocket,
} from 'hooks/use-videocall-websocket';
import { ModalWarning } from 'common/modal-warning';
import { Modal } from 'common/modal';
import { ModalPosition } from 'common/modal/modal';
import { WebcamPreview } from './webcam-preview';
import { WaitingRoomSensorSelection } from './waiting-room-sensor-selection';
import styles from './video-call-waiting-room.module.scss';

type VideoCallWaitingRoomProps = {
  vault: VaultType;
  // The album the interview was started from (its planner): the call then
  // asks only that album's prompts. Without it, the whole vault's.
  albumId?: number;
  onClose?: () => void;
  upgradeVault?: () => void;
  isGuest?: boolean;
};

const VideoCallWaitingRoom: React.FC<VideoCallWaitingRoomProps> = ({
  vault,
  albumId,
  onClose = () => {},
  upgradeVault,
  isGuest = false,
}) => {
  const [showMediaErrorModal, setShowMediaErrorModal] =
    useState<boolean>(false);

  const [showModalSignUp, setShowModalSignUp] = useState<boolean>(false);
  const { state } = useContext(AppContext);

  const { getCallInformation, callInformation, online } = useVideocallWebsocket(
    vault?.id || null,
    vault.videoCallProps?.videoCallToken || null,
  );

  const videocallInterviewerName = callInformation?.videocallInterviewerName;
  const videocallRespondentName = callInformation?.videocallRespondentName;
  const videocallInterviewerId = callInformation?.videocallInterviewerId;
  const videocallRespondentId = callInformation?.videocallRespondentId;
  const videocallRespondentIsGuest =
    callInformation?.videocallRespondentIsGuest;

  const { user } = state.data;
  const { subscriptionsScheme } = state;

  const {
    sensorSelection: {
      microphone: { error: microphoneError },
      camera: { error: cameraError },
    },
  } = useContext(SensorSelectionContext);

  useLoadSensorSelection();
  useEffect(() => {
    if (online) {
      getCallInformation();
    }
  }, [online]);

  const missingFunctionality = !!microphoneError || !!cameraError;

  useEffect(() => {
    setShowMediaErrorModal(missingFunctionality);
  }, [missingFunctionality]);

  useEffect(() => {
    if (
      isTemporaryUser(user) &&
      vault.videoCallProps?.usedCallTime === vault.videoCallProps?.maxCallTime
    ) {
      setShowModalSignUp(true);
    }
  }, []);

  const warningContent = () => {
    const { hours, minutes } = calculateHoursAndMinutes(
      Number(vault.videoCallProps!.maxCallTime),
    );
    if (isFreeTrial(vault)) {
      return (
        <p className="text__body__regular__tiny__gray700">
          Trial vaults have access to {minutes} minutes of video call time. To
          access additional time, please upgrade your subscription
        </p>
      );
    }
    return (
      <p className="text__body__regular__tiny__gray700">
        {hours} hours video recording time per vault per subscription year is
        included. You can{' '}
        <button
          className={classnames(
            'text__body__regular__tiny__secondary50',
            styles.underLine,
          )}
          type="button"
        >
          purchase additional time
        </button>
        {canUpgradeVault(vault, subscriptionsScheme) ? (
          <>
            {' or '}
            <button
              className={classnames(
                'text__body__regular__tiny__secondary50',
                styles.underLine,
              )}
              type="button"
              onClick={upgradeVault}
            >
              upgrade your subscription.
            </button>
          </>
        ) : (
          '.'
        )}
      </p>
    );
  };

  const showRunningOutWarning = () => (
    <HelperText
      icon={<AlertTriangleIcon className={styles.alertRunningOutTimeIcon} />}
      content={warningContent()}
      className={styles.runningOutWarning}
    />
  );

  const showTime = (hours: number, minutes: number) => (
    <div
      className={classnames(
        styles.hoursAndMinutes,
        'text__body__regular__small__gray800',
      )}
    >
      {hours > 0 && (
        <div>
          <span>{hours}</span>
          <span>&nbsp;</span>
          <span>{hours > 1 || hours === 0 ? 'hours' : 'hour'}</span>
        </div>
      )}

      {hours > 0 && <span>,&nbsp; </span>}

      <div>
        <span>{minutes}</span>
        <span>&nbsp;</span>
        <span> minutes </span>
      </div>
    </div>
  );

  const showProgressBar = (alb: VaultType) => {
    const usedCallTime = Number(alb.videoCallProps!.usedCallTime);
    const maxCallTime = Number(alb.videoCallProps!.maxCallTime);

    const videoPercentageUsed = (100 * usedCallTime!) / maxCallTime!;

    const remainingCallTime = maxCallTime - usedCallTime;
    const { hours: usedHours, minutes: usedMinutes } =
      calculateHoursAndMinutes(usedCallTime);
    const { hours: remainingHours, minutes: remainingMinutes } =
      calculateHoursAndMinutes(remainingCallTime);

    return (
      <div className={styles.progressSection}>
        <div
          className={classnames(
            styles.progressTitle,
            'text__body__regular__overline__gray700',
          )}
        >
          vault’s ANNUAL video call time
        </div>
        <div>
          <ProgressBar
            percentageUsed={videoPercentageUsed}
            reachingLimit={
              videoPercentageUsed > msMaxPercentageToAlertVideoCallTime
            }
          />
        </div>
        <div className={classnames(styles.progressTimes)}>
          <span className={styles.usedTime}>
            <span className="text__body__regular__small__gray700">
              Used time:&nbsp;
            </span>
            {showTime(usedHours, usedMinutes)}
          </span>

          <span className={styles.maxTime}>
            <span className="text__body__regular__small__gray700">
              {' '}
              Remaining:&nbsp;
            </span>
            {showTime(remainingHours, remainingMinutes)}
          </span>
        </div>

        {user.permissions?.permissions.upgradeVaults &&
          videoPercentageUsed > msMaxPercentageToAlertVideoCallTime &&
          showRunningOutWarning()}
      </div>
    );
  };

  const persistentInstructions = calculateWaitingRoomPersistentInstructions(
    microphoneError,
    cameraError,
  );

  const [errorModalHeader, errorModalContent] =
    calculateWaitingRoomModalInstructions(microphoneError, cameraError);
  const showMediaErrorModalComponent = () => (
    <InfoModal
      className={styles.modalMediaError}
      title="Camera & Mic"
      content={
        <div className={styles.modalMediaErrorContent}>
          <HelperText
            icon={<AlertTriangleIcon className={styles.modalMediaErrorIcon} />}
            content={
              <span className="text__body__regular__large__textNeutral50">
                {errorModalHeader}
              </span>
            }
          />
          <p>{errorModalContent}</p>
        </div>
      }
      onClose={() => setShowMediaErrorModal(false)}
    />
  );

  const showNotAllowWarning = () => (
    <div className="text__body__regular__tiny__gray700">
      You can’t join the call without a working camera and mic.
    </div>
  );

  const showInterviewTipSection = () => (
    <HelperText
      icon={
        <span className={styles.iconWrapper}>
          <InfoIcon className={styles.textHelpIcon} />
        </span>
      }
      content={
        <div className={styles.interviewTipContent}>
          <span className="text__body__semi__bold__overline__gray700">
            Interview Tip
          </span>
          <span className="text__body__regular__tiny__gray700">
            Go to <span className={styles.plainterviewText}>Planner</span> to
            select and order your questions. You can also choose custom
            recordings while on the call.
          </span>
        </div>
      }
      className={styles.interviewTipWrapper}
    />
  );

  const showCallMembersInfo = (
    currentUserIsGuest: boolean,
    interviewer?: string,
    respondent?: string,
    respondentIsGuest?: boolean,
  ) => {
    if (interviewer && respondent)
      return (
        <div
          className={classnames(
            styles.callMembersInfo,
            'text__body__regular__medium__gray700',
          )}
        >
          <span className="text__body__semi__bold__medium__brand500">
            {interviewer}
          </span>{' '}
          and{' '}
          <span className="text__body__semi__bold__medium__brand500">
            {respondent}
          </span>{' '}
          are already in this call.
        </div>
      );

    if (interviewer) {
      return (
        <div
          className={classnames(
            styles.callMembersInfo,
            'text__body__regular__medium__gray700',
          )}
        >
          <span className="text__body__semi__bold__medium__brand500">
            {interviewer}
          </span>{' '}
          {`has already joined the call as interviewer. ${
            currentUserIsGuest
              ? ''
              : 'If you join as interviewer they will become the respondent.'
          }`}
        </div>
      );
    }

    return (
      <div
        className={classnames(
          styles.callMembersInfo,
          'text__body__regular__medium__gray700',
        )}
      >
        <span className="text__body__semi__bold__medium__brand500">
          {respondent}
        </span>{' '}
        {`has already joined the call as respondent. ${
          respondentIsGuest
            ? ''
            : 'If you join as respondent they will become the interviewer.'
        }`}
      </div>
    );
  };

  const showCallDescription = () => {
    if (isGuest) {
      return (
        <p className={classnames('text__body__regular__small__gray700')}>
          Join as{' '}
          <span className="text__body__semi__bold__small__gray700">
            respondent
          </span>{' '}
          to answer the questions.
        </p>
      );
    }

    if (videocallRespondentIsGuest) {
      return (
        <p className={classnames('text__body__regular__small__gray700')}>
          Join as{' '}
          <span className="text__body__semi__bold__small__gray700">
            interviewer
          </span>{' '}
          to lead the interview.
        </p>
      );
    }

    return (
      <>
        <p className={classnames('text__body__regular__small__gray700')}>
          Join as interviewer if you want to lead the interview, or join as
          respondent to answer the questions.
        </p>
        <p className={classnames('text__body__regular__small__gray700')}>
          Send the{' '}
          <span className="text__body__semi__bold__small__gray700">
            invite link
          </span>{' '}
          to invite a contributor to join the call.
        </p>
      </>
    );
  };

  const showModalSignUpVideoCallComponent = () => (
    <ModalWarning
      title="Sign up to save your recordings"
      content={
        <div className={styles.modalSignUpInfo}>
          <div className="text__body__regular__medium__gray800">
            You’ve run out of video call time! Please complete the sign up
            process to be able to save your clips and access them later,
            download or share them, as well as access the rest of our features.
          </div>
          <div className="text__body__regular__medium__gray800">
            Don’t worry, your first vault is{' '}
            <span className="text__body__semi__bold__medium__gray800">
              free
            </span>{' '}
            for a month, and signing up only takes 2 minutes!
          </div>
        </div>
      }
      successButtonText="Sign up"
      closeFn={() => setShowModalSignUp(false)}
      successFn={() => goToPage(RouteName.SignUp)}
      successStyleButton={ButtonStyle.PrimaryFilled}
    />
  );

  // The call's address carries the album (?album=), so a reload keeps it.
  const albumQuery = albumId ? { album: albumId } : undefined;

  const showContent = () => {
    const { maxCallTime, usedCallTime } = vault.videoCallProps!;
    const timeLimitReached = usedCallTime! >= maxCallTime!;
    const joinCallDisabled =
      !vault.videoCallProps?.videoCallToken ||
      !!microphoneError ||
      !!cameraError ||
      timeLimitReached ||
      (!!videocallInterviewerId &&
        (!!videocallRespondentId || videocallRespondentIsGuest));
    const joinAsRespondentDisabled =
      joinCallDisabled ||
      videocallRespondentIsGuest ||
      (!isGuest && !user.permissions?.permissions.joinVideocallAsRespondent);

    return (
      <div className={styles.body}>
        <div className={styles.joinColumn}>
          <div className={styles.joinTitleAndButtons}>
            <div className={styles.joinTitle}>
              <span className={classnames('text__heading4__gray800')}>
                Start video call
              </span>
              {/* TODO: For now, we use <CopyLink /> component. Previously we were using <SendLinkToContributor />  */}
              <CopyLink
                buttonStyle={ButtonStyle.PrimaryGhost}
                buttonSize={ButtonSize.Small}
                className={styles.copyLinkButton}
                path={`${apiBaseURL}/vaults/${vault.id}/join/${vault
                  .videoCallProps?.videoCallToken!}`}
              >
                <LinkIcon className={styles.linkIcon} />
                <div className={styles.linkText}>Invite link</div>
              </CopyLink>
            </div>

            {showCallDescription()}

            {(videocallInterviewerName ||
              videocallRespondentName ||
              videocallRespondentIsGuest) &&
              showCallMembersInfo(
                isGuest,
                videocallInterviewerName,
                videocallRespondentName,
                videocallRespondentIsGuest,
              )}

            <div className={styles.buttons}>
              <Button
                buttonSize={ButtonSize.Medium}
                buttonType={ButtonType.Submit}
                disabled={joinCallDisabled || isGuest}
                onClick={() => {
                  goToPage(
                    RouteName.AgoraVideoCall,
                    {
                      agoraVaultId: vault.id,
                      videoCallToken: vault.videoCallProps?.videoCallToken!,
                      role: VideoCallRole.INTERVIEWER,
                    },
                    albumQuery,
                  );
                }}
              >
                Join as interviewer
              </Button>
              <Button
                buttonStyle={ButtonStyle.PrimaryStroke}
                buttonSize={ButtonSize.Medium}
                disabled={joinAsRespondentDisabled}
                onClick={() => {
                  goToPage(
                    RouteName.AgoraVideoCall,
                    {
                      agoraVaultId: vault.id,
                      videoCallToken: vault.videoCallProps?.videoCallToken!,
                      role: VideoCallRole.RESPONDENT,
                    },
                    albumQuery,
                  );
                }}
                tooltip={
                  !isGuest &&
                  !user.permissions?.permissions.joinVideocallAsRespondent
                }
                tooltipContent="Available after sign up"
                tooltipId="join-as-respondent-tooltip"
              >
                Join as respondent
              </Button>
            </div>

            {!isOwner(user, vault) && cameraError && showNotAllowWarning()}

            {!isGuest && showProgressBar(vault)}

            {!isGuest && showInterviewTipSection()}
          </div>
        </div>

        <div className={styles.cameraColumn}>
          <div className={styles.cameraBody}>
            <WebcamPreview className={styles.cameraStyles} />
          </div>
          <div className={styles.instructionsContainer}>
            <HelperText
              icon={
                missingFunctionality ? (
                  <AlertTriangleIcon className={styles.instructionsHelpIcon} />
                ) : (
                  <InfoIcon className={styles.instructionsHelpIcon} />
                )
              }
              content={persistentInstructions}
            />
          </div>
          <div className={styles.sensorSelectionContainer}>
            <WaitingRoomSensorSelection />
          </div>
        </div>
      </div>
    );
  };
  return (
    <>
      {showMediaErrorModal && showMediaErrorModalComponent()}
      {showModalSignUp && showModalSignUpVideoCallComponent()}
      <Modal
        contentClassName={styles.modalContainer}
        position={ModalPosition.Center}
        onClose={onClose}
      >
        <div className={styles.fullcontent}>{showContent()}</div>
      </Modal>
    </>
  );
};

export { VideoCallWaitingRoom };
